import { FC, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useTranslation, Trans } from 'react-i18next';
import PinInput from 'react-pin-input';
import clsx from 'clsx';

import { IconButton } from '@material-ui/core';
import useModal from '@yaydoo/react-components/lib/hooks/useModal';
import useToast from '@yaydoo/react-components/lib/hooks/useToast';
import CloseIcon from '@yaydoo/react-components/lib/icons/Close';

import { IKushkiPaymentData } from '@containers/checkout/payment-form/types';
import OysterButton from '@components/common/Button';
import Loader from '@components/common/Loader';
import useStore from '@hooks/useStore';
import useStyles from './styles';

interface IValidateOtpForCard {
  data: IKushkiPaymentData;
  apiKey: string;
}

interface IError {
  message?: string;
}

const ValidateOtpForCard: FC<IValidateOtpForCard> = ({ data, apiKey }) => {
  const { closeModal } = useModal();
  const { t } = useTranslation();
  const css = useStyles();
  const { openToast } = useToast();
  const { kushki } = useStore();
  const isValidOtp = kushki.card.code.length === 3;

  const onUpdateOtp = (code: string) => {
    kushki.card.updateField('code', code);
  };

  const onPreauthorize = async () => {
    try {
      await kushki.card.preauthorizeFromKushki(data, apiKey);
    } catch (error) {
      const err = error as IError;
      openToast({
        type: 'error',
        message: t(`checkoutScreen.payment.errors.${err?.message || 'OTP200'}`),
        title: 'Error',
      });
    }
  };

  const onClose = () => {
    /**
     * Temporal fix, we should be migrating from using update functions as it
     * defeats the purpose protection of the store (not mutating directly)
     * */
    kushki.updateField('flags.isEnteringCodeForPreauth', false);
    closeModal();
  };

  const { requestingPreauthorization } = kushki.flags;

  const verifyButtonClassName = clsx(css.verifyButton, {
    [css.invisible]: requestingPreauthorization,
  });

  const loaderClassName = clsx(css.loader, {
    [css.hide]: !requestingPreauthorization,
  });

  useEffect(() => {
    if (!kushki.flags.isEnteringCodeForPreauth) {
      closeModal();
    }
  }, [kushki.flags.isEnteringCodeForPreauth]);

  return (
    <div className={css.modal}>
      <div className={css.modalContainer}>
        <IconButton onClick={onClose} className={css.closeButton}>
          <CloseIcon defaultColor />
        </IconButton>
        <h3>
          {requestingPreauthorization
            ? t('checkoutScreen.payment.methods.card.verifying')
            : t('checkoutScreen.payment.methods.card.verificationCode')}
        </h3>

        <div className={css.modalContent}>
          <Loader className={loaderClassName} size="small" />

          <p>
            <Trans i18nKey="checkoutScreen.payment.methods.card.enterCode">
              <strong />
            </Trans>
          </p>

          <div className={css.memo}>
            <span>{t('checkoutScreen.payment.methods.card.paidWithOyster')}</span>
            <strong>$ X.XX</strong>
          </div>

          <p>{t('checkoutScreen.payment.methods.card.chargeWillBeVoided')}</p>

          <div className={css.pin}>
            <PinInput
              length={3}
              initialValue=""
              focus
              onChange={onUpdateOtp}
              type="numeric"
              inputMode="number"
            />
          </div>

          <OysterButton
            data-testid="create-account-button"
            size="large"
            className={verifyButtonClassName}
            onClick={onPreauthorize}
            disabled={requestingPreauthorization || !isValidOtp}
          >
            {t('checkoutScreen.payment.methods.card.verify')}
          </OysterButton>
        </div>
      </div>
    </div>
  );
};

export default observer(ValidateOtpForCard);
